import {
  Flex,
  Grid,
  Heading,
  Icon,
  IconButton,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { FaCheck, FaCheckDouble, FaPlus, FaTrash } from "react-icons/fa";
import { TcmEncounter } from "../../../api/tcm-encounter";
import { formatDateTimeInLocalNoYear } from "../../shared/date";
import { Button } from "../shared-components/button";
import { OutreachAction } from "../transitions-of-care/outreach-actions";

type OutreachLogsProps = {
  encounter: TcmEncounter;
  onOutreachLogUpdate: (status: "Attempted" | "Completed", action: OutreachAction) => void;
};

export function OutreachLogs({ encounter, onOutreachLogUpdate }: OutreachLogsProps) {
  const outreachColor = useColorModeValue("gray.800", "white");
  const outreachBorder = useColorModeValue("gray.200", "gray.600");
  const outreachBg = useColorModeValue("gray.50", "gray.700");
  const outreachTextColor = useColorModeValue("gray.600", "gray.300");
  const outreachIconColor = useColorModeValue("gray.500", "gray.400");
  const outreachIconHoverColor = useColorModeValue("red.500", "red.300");

  if (!encounter.outreachLogs) {
    return null;
  }

  // Get the latest status to determine if Completed button should be disabled
  const latestStatus = encounter.outreachLogs[encounter.outreachLogs.length - 1]?.status;
  const isAlreadyCompleted = latestStatus === "Completed";

  return (
    <VStack align="stretch" spacing={3}>
      <Flex gap={2} align="center">
        <Heading size="md" color={outreachColor}>
          Outreach Log
        </Heading>
        <Button
          size="xs"
          variant="outline"
          onClick={() => onOutreachLogUpdate("Attempted", { type: "add", status: "Attempted" })}
        >
          <Icon as={FaPlus} fontSize="xs" mr={1} />
          Attempted
        </Button>
        <Button
          size="xs"
          variant="outline"
          isDisabled={isAlreadyCompleted}
          onClick={() => onOutreachLogUpdate("Completed", { type: "add", status: "Completed" })}
        >
          <Icon as={FaPlus} fontSize="xs" mr={1} />
          Completed
        </Button>
      </Flex>

      {encounter.outreachLogs.length > 0 ? (
        <VStack align="stretch" spacing={1}>
          {encounter.outreachLogs
            .map((log, idx) => ({ log, idx }))
            .reverse()
            .map(({ log, idx }) => (
              <Flex
                key={`${log.timestamp}-${idx}`}
                justify="space-between"
                align="center"
                p={"1px 10px"}
                borderWidth={1}
                borderRadius="md"
                borderColor={outreachBorder}
                bg={outreachBg}
                minH="auto"
              >
                <Grid templateColumns="0.3fr 1fr 1fr" alignItems="center" w="fit-content">
                  {log.status === "Attempted" ? (
                    <Icon as={FaCheck} color={"gray"} fontSize="xs" />
                  ) : (
                    <Icon as={FaCheckDouble} color={"green"} fontSize="xs" />
                  )}
                  <Text fontWeight="medium" fontSize="sm">
                    {log.status}
                  </Text>
                  <Text fontSize="xs" color={outreachTextColor}>
                    {formatDateTimeInLocalNoYear(new Date(log.timestamp))}
                  </Text>
                </Grid>
                <IconButton
                  aria-label="Delete log"
                  icon={<FaTrash />}
                  size="sm"
                  variant="ghost"
                  color={outreachIconColor}
                  _hover={{ color: outreachIconHoverColor }}
                  onClick={() => onOutreachLogUpdate("Attempted", { type: "delete", index: idx })}
                />
              </Flex>
            ))}
        </VStack>
      ) : (
        <Text color={outreachTextColor} fontSize="sm">
          No outreach logs yet. Click "Attempted" or "Completed" to add entries.
        </Text>
      )}
    </VStack>
  );
}
